var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import "./code-input";
const ACHI = /^\d{5}-\d{2}$/;
const isDx = (c) => c.classification === "icd10am" || c.type === "Pdx" || c.type === "Adx"
    || (!c.classification && !c.type && !ACHI.test(c.code));
/** Client-side mirror of the server's apply_changes, for the "your corrected
 *  coding" preview only. The server derives the graded set itself. */
export function applyChanges(locked, changes) {
    let dx = locked.filter(isDx).map((c) => ({ ...c }));
    let pr = locked.filter((c) => !isDx(c)).map((c) => ({ ...c }));
    let principal = null;
    for (const ch of changes) {
        if (ch.action === "confirm" || !ch.code)
            continue;
        if (ch.action === "remove") {
            dx = dx.filter((c) => c.code !== ch.code);
            pr = pr.filter((c) => c.code !== ch.code);
        }
        else if (ch.action === "amend") {
            const apply = (c) => c.code === ch.code
                ? { ...c, code: ch.new_code?.trim() || c.code, description: ch.description ?? c.description,
                    ...(ch.new_cof ? { cof: ch.new_cof } : {}) }
                : c;
            dx = dx.map(apply);
            pr = pr.map(apply);
        }
        else if (ch.action === "resequence") {
            principal = ch.code;
        }
        else if (ch.action === "reorder") {
            const i = dx.findIndex((c) => c.code === ch.code);
            if (i >= 0) {
                const [card] = dx.splice(i, 1);
                const pos = Math.max(0, Math.min(ch.position ?? dx.length, dx.length));
                dx.splice(pos, 0, card);
            }
        }
        else if (ch.action === "add") {
            const card = { code: ch.code, description: ch.description, type: ACHI.test(ch.code) ? "Aproc" : "Adx" };
            if (!ACHI.test(ch.code)) {
                card.cof = ch.cof ?? 2;
                if (!dx.some((c) => c.code === ch.code))
                    dx.push(card);
            }
            else if (!pr.some((c) => c.code === ch.code)) {
                pr.push(card);
            }
            if (ch.as_principal)
                principal = ch.code;
        }
    }
    if (principal) {
        const i = dx.findIndex((c) => c.code === principal);
        if (i > 0) {
            const [card] = dx.splice(i, 1);
            dx.unshift(card);
        }
    }
    return [
        ...dx.map((c, i) => ({ ...c, classification: "icd10am", type: i === 0 ? "Pdx" : "Adx" })),
        ...pr.map((c, i) => ({ ...c, classification: "achi", type: i === 0 ? "Pproc" : "Aproc" })),
    ];
}
/** Student-side items count, exactly as the procedure text defines it:
 *  each corrected code, each removed code, and one onset-flag check for each
 *  diagnosis in both the locked and the corrected list. */
export function countItems(locked, changes) {
    const corrected = applyChanges(locked, changes);
    const removed = changes.filter((c) => c.action === "remove" && c.code).length;
    const lockedDx = new Set(locked.filter(isDx).map((c) => c.code));
    const both = corrected.filter((c) => isDx(c) && lockedDx.has(c.code)).length;
    const n = changes.filter((c) => c.action !== "confirm" && c.code).length;
    return { changes: n, items: corrected.length + removed + both };
}
let OwnWorkAuditRecord = class OwnWorkAuditRecord extends LitElement {
    constructor() {
        super(...arguments);
        this.locked = [];
        this.changes = [];
        this.labels = { error_types: [], impacts: [], causes: [] };
        this.edition = "13e";
        this.readonly = false;
    }
    static { this.styles = css `
    :host, *, *::before, *::after { box-sizing: border-box; }
    :host { display: block; font-size: 13px; color: var(--color-slate-700); }
    h2 { font-size: 13px; font-weight: 700; margin: 0 0 3px; text-transform: uppercase; letter-spacing: .05em;
         color: var(--color-slate-700); }
    .sub { font-size: 12px; color: var(--color-slate-500); margin-bottom: 12px; line-height: 1.45; }
    .section { border: 1px solid var(--color-slate-200); border-radius: var(--radius-md, 8px);
               background: var(--color-white); margin-bottom: 14px; overflow: hidden; }
    .head { padding: 9px 12px; background: var(--color-slate-50); border-bottom: 1px solid var(--color-slate-200);
            font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: var(--color-slate-700); }
    .row { padding: 10px 12px; border-bottom: 1px solid var(--color-slate-100); }
    .row:last-child { border-bottom: 0; }
    .line { display: flex; gap: 8px; align-items: baseline; flex-wrap: wrap; }
    .code { font-family: var(--font-mono); font-weight: 700; color: var(--color-slate-900); }
    .badge { display: inline-block; font-size: 10px; font-weight: 700; padding: 2px 6px; border-radius: 10px;
             letter-spacing: .04em; text-transform: uppercase; }
    .badge.p { background: var(--color-blue-600); color: white; }
    .badge.a { background: var(--color-slate-100); color: var(--color-slate-600); }
    .desc { color: var(--color-slate-600); flex: 1; min-width: 120px; }
    .flag { font-size: 11px; color: var(--color-slate-500); }
    .controls { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 10px; margin-top: 8px; }
    .controls label { display: flex; flex-direction: column; gap: 2px; font-size: 11px; color: var(--color-slate-500);
                      text-transform: uppercase; letter-spacing: .04em; font-weight: 600; }
    .controls select, .controls input[type=text] { font: inherit; font-size: 12px; padding: 4px 6px; border-radius: 4px;
                      border: 1px solid var(--color-slate-300); color: var(--color-slate-800); background: white;
                      text-transform: none; letter-spacing: 0; font-weight: 400; }
    .wide { grid-column: 1 / -1; }
    .actions { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 8px; }
    .actions button { font: inherit; font-size: 12px; padding: 4px 10px; border-radius: 999px; cursor: pointer;
                      border: 1px solid var(--color-slate-300); background: var(--color-white); color: var(--color-slate-700); }
    .actions button.on { background: var(--color-blue-600); border-color: var(--color-blue-600); color: white; }
    .actions button.danger.on { background: #dc2626; border-color: #dc2626; }
    .actions button:disabled { opacity: .5; cursor: default; }
    .order { display: inline-flex; gap: 2px; margin-left: auto; }
    .order button { padding: 2px 6px; font-size: 11px; }
    .pending { color: var(--color-amber-600, #d97706); font-size: 11px; font-weight: 600; }
    .add { padding: 10px 12px; }
    .preview { padding: 10px 12px; }
    .preview .code { font-weight: 600; }
    .preview div { padding: 2px 0; }
    .count { font-size: 12px; color: var(--color-slate-500); padding: 0 12px 10px; }
  `; }
    emit(changes) {
        this.changes = changes;
        this.dispatchEvent(new CustomEvent("changes-change", { detail: { changes }, bubbles: true, composed: true }));
    }
    changeFor(code) {
        return this.changes.find((c) => c.code === code && c.action !== "reorder" && c.action !== "add");
    }
    setChange(code, patch) {
        const existing = this.changeFor(code);
        const others = this.changes.filter((c) => c !== existing);
        const next = { ...(existing ?? { action: "confirm", code }), ...patch };
        if (next.action === "confirm") {
            delete next.new_code;
            delete next.new_cof;
            delete next.error_type;
            delete next.impact;
            delete next.cause;
        }
        this.emit([...others, next]);
    }
    setAdded(index, patch) {
        const adds = this.changes.filter((c) => c.action === "add");
        const target = adds[index];
        if (!target)
            return;
        if (patch === null) {
            this.emit(this.changes.filter((c) => c !== target));
            return;
        }
        this.emit(this.changes.map((c) => (c === target ? { ...c, ...patch } : c)));
    }
    reorder(code, delta) {
        const corrected = applyChanges(this.locked, this.changes);
        const dx = corrected.filter(isDx);
        const i = dx.findIndex((c) => c.code === code);
        const j = i + delta;
        if (i <= 0 || j <= 0 || j >= dx.length)
            return; // the principal is moved with "make principal"
        const others = this.changes.filter((c) => !(c.action === "reorder" && c.code === code));
        const prior = this.changes.find((c) => c.action === "reorder" && c.code === code);
        this.emit([...others, { ...(prior ?? { action: "reorder", code }), action: "reorder", position: j }]);
    }
    onAddPick(e) {
        const { code, description } = e.detail;
        if (this.locked.some((c) => c.code === code) || this.changes.some((c) => c.action === "add" && c.code === code))
            return;
        this.emit([...this.changes, { action: "add", code, description, cof: 2 }]);
    }
    renderClassification(ch, update) {
        const sel = (key, label, options, placeholder) => html `
      <label>${label}
        <select aria-label="${label}" .value=${ch[key] ?? ""} ?disabled=${this.readonly}
                @change=${(e) => update({ [key]: e.target.value || undefined })}>
          <option value="">${placeholder}</option>
          ${options.map((o) => html `<option value=${o.id} ?selected=${ch[key] === o.id}>${o.label}</option>`)}
        </select>
      </label>`;
        return html `
      ${sel("error_type", "Error type", this.labels.error_types, "Choose the error type")}
      ${sel("impact", "Data impact", this.labels.impacts, "Choose the data impact")}
      ${sel("cause", "Likely cause", this.labels.causes, "Choose the likely cause")}`;
    }
    renderLocked(card, index, dxCount) {
        const ch = this.changeFor(card.code);
        const action = ch?.action ?? null;
        const dx = isDx(card);
        const badge = card.type === "Pdx" ? html `<span class="badge p">Principal</span>`
            : card.type === "Pproc" ? html `<span class="badge p">Principal procedure</span>`
                : html `<span class="badge a">${dx ? "Additional" : "Additional procedure"}</span>`;
        const btn = (a, label, cls = "") => html `
      <button type="button" class="${action === a ? "on" : ""} ${cls}" ?disabled=${this.readonly}
              data-action=${a} data-code=${card.code}
              @click=${() => this.setChange(card.code, { action: a })}>${label}</button>`;
        return html `
      <div class="row">
        <div class="line">
          <span class="code">${card.code}</span>${badge}
          <span class="desc">${card.description ?? ""}</span>
          ${dx ? html `<span class="flag">Onset ${card.cof === 1 ? "arose this admission" : "pre-existing"}</span>` : nothing}
          ${!action ? html `<span class="pending">decide</span>` : nothing}
        </div>
        <div class="actions">
          ${btn("confirm", "Confirm")}
          ${btn("amend", "Amend")}
          ${btn("remove", "Remove", "danger")}
          ${dx && card.type !== "Pdx" ? btn("resequence", "Make principal") : nothing}
          ${dx && card.type !== "Pdx" && dxCount > 2 ? html `
            <span class="order">
              <button type="button" aria-label="Move ${card.code} up" ?disabled=${this.readonly || index <= 1}
                      @click=${() => this.reorder(card.code, -1)}>▲</button>
              <button type="button" aria-label="Move ${card.code} down" ?disabled=${this.readonly || index >= dxCount - 1}
                      @click=${() => this.reorder(card.code, 1)}>▼</button>
            </span>` : nothing}
        </div>
        ${action === "amend" ? html `
          <div class="controls">
            <label class="wide">Replacement code (leave blank to keep ${card.code} and change only the onset flag)
              <code-input .edition=${this.edition} classification=${dx ? "icd10am" : "achi"}
                          placeholder="New code"
                          @code-pick=${(e) => this.setChange(card.code, { new_code: e.detail.code, description: e.detail.description })}></code-input>
              ${ch?.new_code ? html `<span class="flag">Replacing with <b>${ch.new_code}</b> ${ch.description ?? ""}</span>` : nothing}
            </label>
            ${dx ? html `<label>Onset flag
              <select aria-label="Onset flag" .value=${String(ch?.new_cof ?? card.cof ?? 2)} ?disabled=${this.readonly}
                      @change=${(e) => this.setChange(card.code, { new_cof: parseInt(e.target.value, 10) })}>
                <option value="2">Pre-existing</option>
                <option value="1">Arose this admission</option>
              </select></label>` : nothing}
          </div>` : nothing}
        ${action && action !== "confirm" ? html `<div class="controls">
          ${this.renderClassification(ch, (patch) => this.setChange(card.code, patch))}
        </div>` : nothing}
      </div>`;
    }
    renderAdded(ch, index) {
        const dx = isDx({ code: ch.code });
        return html `
      <div class="row">
        <div class="line">
          <span class="code">${ch.code}</span><span class="badge a">Added</span>
          <span class="desc">${ch.description ?? ""}</span>
          <button type="button" class="rm" ?disabled=${this.readonly} aria-label="Remove added ${ch.code}"
                  @click=${() => this.setAdded(index, null)}>×</button>
        </div>
        <div class="controls">
          ${dx ? html `
            <label>Onset flag
              <select aria-label="Onset flag for ${ch.code}" ?disabled=${this.readonly} .value=${String(ch.cof ?? 2)}
                      @change=${(e) => this.setAdded(index, { cof: parseInt(e.target.value, 10) })}>
                <option value="2">Pre-existing</option>
                <option value="1">Arose this admission</option>
              </select></label>
            <label>Position
              <select aria-label="Position for ${ch.code}" ?disabled=${this.readonly} .value=${ch.as_principal ? "principal" : "additional"}
                      @change=${(e) => this.setAdded(index, { as_principal: e.target.value === "principal" })}>
                <option value="additional">Additional diagnosis</option>
                <option value="principal">Principal diagnosis</option>
              </select></label>` : nothing}
          ${this.renderClassification(ch, (patch) => this.setAdded(index, patch))}
        </div>
      </div>`;
    }
    render() {
        const dxCards = this.locked.filter(isDx);
        const added = this.changes.filter((c) => c.action === "add");
        const corrected = applyChanges(this.locked, this.changes);
        return html `
      <h2>Audit your locked coding</h2>
      <div class="sub">For each code decide: confirm it, amend it, remove it, or make it the principal diagnosis.
        Add anything the record supports that you missed. Every change needs an error type; add the data impact and
        likely cause where you can judge them. Your locked codes cannot be edited directly.</div>
      <div class="section">
        <div class="head">Your locked coding</div>
        ${this.locked.map((c, i) => this.renderLocked(c, isDx(c) ? dxCards.indexOf(c) : i, dxCards.length))}
        ${this.locked.length === 0 ? html `<div class="row">No codes were locked for this record.</div>` : nothing}
      </div>
      <div class="section">
        <div class="head">Codes you missed</div>
        ${added.map((c, i) => this.renderAdded(c, i))}
        ${this.readonly ? nothing : html `<div class="add">
          <code-input .edition=${this.edition} placeholder="ICD-10-AM or ACHI code the record supports"
                      @code-pick=${(e) => this.onAddPick(e)}></code-input>
        </div>`}
      </div>
      <div class="section">
        <div class="head">Your corrected coding</div>
        <div class="preview">
          ${corrected.map((c) => html `<div><span class="code">${c.code}</span> ${c.type}${isDx(c) ? ` · onset ${c.cof === 1 ? "arose this admission" : "pre-existing"}` : ""} ${c.description ?? ""}</div>`)}
        </div>
        <div class="count">Count your own items for the error rate: one for each code in the corrected list, one for each
          code you removed, and one for the onset flag of each diagnosis that is in both your locked list and your
          corrected list.</div>
      </div>`;
    }
};
__decorate([
    property({ attribute: false })
], OwnWorkAuditRecord.prototype, "locked", void 0);
__decorate([
    property({ attribute: false })
], OwnWorkAuditRecord.prototype, "changes", void 0);
__decorate([
    property({ attribute: false })
], OwnWorkAuditRecord.prototype, "labels", void 0);
__decorate([
    property({ type: String })
], OwnWorkAuditRecord.prototype, "edition", void 0);
__decorate([
    property({ type: Boolean })
], OwnWorkAuditRecord.prototype, "readonly", void 0);
OwnWorkAuditRecord = __decorate([
    customElement("own-work-audit-record")
], OwnWorkAuditRecord);
export { OwnWorkAuditRecord };
